import { Banner } from '@buffer-mono/popcorn'
import { addDays } from 'date-fns'
import { useEffect, useMemo, useRef } from 'react'
import type { JSX } from 'react'
import { useUser } from '../../../../common/context/User'
import { MODAL_ACTIONS } from '../../../../common/events/modalEvents'
import useDismissObject from '../../../../common/hooks/useDismissObject'
import { trackAnnualUpgradeEligible } from '../../../../common/hooks/useSegmentTracking'
import { MODALS } from '../../../../common/types'
import { usePlanSelectorQuery } from '../../../../components/PlanSelector/hooks/usePlanSelectorQuery'
import { BillingIntervalValues } from '../../../../components/PlanSelector/types'
import type { PlanOption } from '../../../../gql/graphql'
import {
  calculateTierPrice,
  calculateYearlySavings,
  formatPrice,
} from '../../../../components/PlanSelector/utils/pricingCalculations'

const AnnualUpgradeBanner = ({
  treatment,
}: {
  treatment: string
}): JSX.Element | null => {
  const user = useUser()
  const { data: planData, loading } = usePlanSelectorQuery()

  const subscription =
    planData?.__typename === 'PlanSelectorData' ? planData.subscription : null
  const availablePlans =
    planData?.__typename === 'PlanSelectorData' ? planData.availablePlans : null

  const currentPlanId = subscription?.plan?.id
  const quantity = subscription?.quantity ?? 0

  const monthlyPlan = useMemo(
    () =>
      availablePlans?.find(
        (p: PlanOption) =>
          p.planId === currentPlanId &&
          p.planInterval === BillingIntervalValues.month,
      ),
    [availablePlans, currentPlanId],
  )

  const annualPlan = useMemo(
    () =>
      availablePlans?.find(
        (p: PlanOption) =>
          p.planId === currentPlanId &&
          p.planInterval === BillingIntervalValues.year,
      ),
    [availablePlans, currentPlanId],
  )

  const pricingData = useMemo(() => {
    if (!monthlyPlan || !annualPlan) return null

    const currency = monthlyPlan.pricing[0]?.currency ?? 'USD'
    const monthlyCalc = calculateTierPrice(monthlyPlan.pricing, quantity)
    const annualCalc = calculateTierPrice(annualPlan.pricing, quantity)
    const savings = calculateYearlySavings(
      monthlyCalc.totalPrice,
      annualCalc.totalPrice,
    )

    if (savings <= 0) return null

    return {
      currentMonthlyPriceOverYear: monthlyCalc.totalPrice * 12,
      yearlyPrice: annualCalc.totalPrice,
      savingsAmount: savings,
      currency,
      savingsFormatted: formatPrice(savings, currency),
    }
  }, [monthlyPlan, annualPlan, quantity])

  const hasFiredRef = useRef(false)
  useEffect(() => {
    if (hasFiredRef.current || !pricingData) return
    hasFiredRef.current = true

    trackAnnualUpgradeEligible({
      payload: {
        treatment,
        currentMonthlyPriceOverYear: pricingData.currentMonthlyPriceOverYear,
        yearlyPrice: pricingData.yearlyPrice,
        savingsAmount: pricingData.savingsAmount,
        currency: pricingData.currency,
      },
      user,
    })
  }, [pricingData, treatment, user])

  const { onDismissObject } = useDismissObject({
    organizationId: user.currentOrganization?.id,
    dismissedObject: {
      id: 'annual-upgrade-banner',
      expiresAt: addDays(new Date(), 30).toISOString(),
    },
  })

  if (loading || !pricingData) return null

  const handleKeepMonthly = (): void => {
    onDismissObject()
  }

  return (
    <Banner
      variant="info"
      data-testid="annual-upgrade-banner"
      onDismiss={handleKeepMonthly}
    >
      <Banner.Text>
        <strong>A quick way to save on your plan</strong> Save{' '}
        {pricingData.savingsFormatted} per year when you switch to annual
        billing.
      </Banner.Text>
      <Banner.Action
        onClick={(): void => {
          MODAL_ACTIONS.openModal(MODALS.annualUpgrade, {
            cta: 'annualUpgradeModal-banner',
            ctaButton: 'switchToAnnual',
            ctaView: 'annualUpgradeModal',
            ctaLocation: 'banner',
            ctaVersion: '1',
            upgradePathName: 'annualUpgrade',
          })
        }}
      >
        Switch to annual
      </Banner.Action>
      <Banner.Action variant="tertiary" onClick={handleKeepMonthly}>
        Keep monthly
      </Banner.Action>
    </Banner>
  )
}

export default AnnualUpgradeBanner
